/**
 * Slot token — the DURABLE, client-minted identity of one day or one exercise slot on the wire.
 *
 * Ported verbatim from `move-with-me-frontend/.../plans/builder/model/slotToken.ts`. The reasoning
 * below is the mobile app's, kept because it records a defect that has already happened.
 *
 * `order` is a POSITION, and a builder renumbers positions on every insert and delete, so "day 3
 * was deleted" and "every later day shifted up one" produce byte-identical trees. The backend
 * cannot tell them apart, and matching positionally made a surviving day inherit a neighbour's
 * identity — which reset its exercises' activation cycle and dropped a client three cycles into a
 * progression back to the authored base. The token is what separates identity from position: a
 * token the stored plan already knows is the SAME slot, moved or not; an unknown one is a NEW
 * slot; and a stored token absent from the payload is a removal.
 *
 * Document-model ADR §3 states the rule this module exists to make unbreakable: **every day and
 * every exercise produced by a clone mints a fresh token.** Because minting is the only way to
 * construct one, a future clone helper inherits that by construction rather than by memory.
 *
 * Two nodes sharing a token is refused server-side with `duplicate_slot_token` rather than
 * silently fused — the danger is real enough that it validates at the boundary.
 *
 * The token is an opaque identity, never a secret: guessing one buys nothing, since the plan is
 * already owner-scoped. So `Math.random` is the right tool and `crypto.randomUUID` would be
 * ceremony. (It would also work here, unlike on the phone where the module has to run under a bare
 * Node test runner with no native crypto — that constraint does not apply in a browser, but the
 * implementation is kept identical so the two apps mint the same SHAPE of token.)
 *
 * At the bottom of the file sits `newSetLocalId`, the one identity here that is NOT on the wire.
 * It is minted next to these because it needs the same uniqueness guarantee, and because keeping
 * both mints in one place is what makes the difference between them a thing a reader meets rather
 * than a thing they have to infer.
 */

/** Wire bound (`PostDayTemplateInput.SlotToken`): 1–64 characters. A mint stays far under it. */
const RANDOM_LENGTH = 10;

/** A fixed-width base-36 chunk. `Math.random()` can stringify short (`0.5` → `"i"`), hence the pad. */
function randomChunk(): string {
  return Math.random().toString(36).slice(2).padEnd(RANDOM_LENGTH, '0').slice(0, RANDOM_LENGTH);
}

/**
 * Monotonic within the session — it is what guarantees two tokens minted in the SAME millisecond
 * differ, so uniqueness never rests on `Math.random()` alone. Bulk mints happen in one tick: a
 * duplicated phase mints one token per day and per exercise beneath it.
 */
let mintCount = 0;

/** Mint a fresh slot token. Every call returns a new one — a duplicated slot is a NEW slot. */
export function newSlotToken(): string {
  mintCount += 1;
  return `s${Date.now().toString(36)}-${mintCount.toString(36)}-${randomChunk()}`;
}

/**
 * The token a day or exercise loaded from OUTSIDE this session should carry: the one it came with,
 * echoed VERBATIM so the backend recognises the slot, or a fresh mint when it came with none.
 *
 * The blank case is real rather than defensive. The backend added these columns
 * `NOT NULL DEFAULT ''` with no backfill, so every day and exercise stored BEFORE that migration
 * reads back as `''`. An empty value cannot be resent — the wire demands 1–64 characters, and
 * every blank sibling would collide as `duplicate_slot_token` — which would leave the plan
 * unsavable. Minting adopts such a slot into the token world on its next save.
 */
export function slotTokenOrMint(stored: string | null | undefined): string {
  return stored != null && stored.trim().length > 0 ? stored : newSlotToken();
}

/**
 * The LOCAL identity of one prescribed set — the one identity in this tree that never reaches the
 * wire, and the reason this module is not only about wire tokens.
 *
 * A set has no durable identity: `PostSetInput` carries no field for one and the backend reads a
 * set list POSITIONALLY, so a set is addressed by `(cycleIndex, setIndex)` and nothing more
 * (document-model ADR §1). That stays true. What the ADR never made a set is *anonymous within a
 * render*: `setIndex` is a position, every insert and delete renumbers it, and a React key built
 * from it names a different row after every structural edit. Deleting the first of four sets then
 * frees the key of the FOURTH — so the exit animation played on the last row while the first one
 * vanished, and a mid-list insert remounted every row below the insertion point, throwing away the
 * caret of a cell being typed into.
 *
 * This is that missing local identity and nothing more. It is minted at the three doors a set can
 * come through (`createSet`, `responseToDraft`, and every operation that inserts or copies one),
 * carried unchanged through every renumber, and dropped by `draftToRequest` — which lists the
 * fields it emits explicitly, so this one cannot leak by being added here. Two loads of the same
 * plan do not agree on it: it identifies a row on screen, not a row in the database. The one copy
 * that keeps it is the device's crash-recovery record of an unsaved plan, which restores the same
 * document and holds these ids unique like the slot tokens.
 *
 * The `l` prefix is what stops the two ever being confused: a slot token starts `s`, and this can
 * therefore never be mistaken for one that could be sent.
 */
export function newSetLocalId(): string {
  mintCount += 1;
  return `l${Date.now().toString(36)}-${mintCount.toString(36)}-${randomChunk()}`;
}
